import { useEffect, useState } from 'react';
import { useToast, toastFromError } from '../../components/ToastProvider';
import { useZoneReadReport } from '../../components/ZoneContext';
import { useWorkingZone } from '../../lib/zones';
import { getGradingMe, type GradingCan } from '../../lib/grading-api';
import { GradersTab } from './grading/GradersTab';
import { BatchesTab } from './grading/BatchesTab';
import { QualityTab } from './grading/QualityTab';
import { RulesTab } from './grading/RulesTab';
import { WorkflowTab } from './grading/WorkflowTab';
import { TestimonialsTab } from './grading/TestimonialsTab';
import { Loading } from './grading/shared';
import './grading/grading.css';
import './grading/grading-product.css';

/**
 * /admin/grading — COG → COMMUNITY → Grading (ADR-133 Amendment 2 §1; COMMUNITY build 3 Stage B, S9 · Community).
 *
 * The people and the process behind the Engagement grade, as the signed-off ops mock (version 13) draws them: the
 * standard tab strip — Grading (Graders · Batches · Quality · Rules) · Workflow · Testimonials (the Outcome stories and their credits —
 * s9-outcome-report, ADR-137 D8) · Rubric and Reference set (not yet designed, inert). CSS is extracted from the mock by script
 * (`portal-staff/harness/grading-extract/extract-ops.mjs`) and scoped under `.nx-grading`.
 *
 * ⛔ WHAT A CALLER MAY DO IS THE SERVICE'S ANSWER. `GET /admin/grading/me` returns the staff Cedar decisions for this
 * person (configure · moderate · queue); a mutator the answer withholds is ABSENT, not disabled — read_only and
 * audit_only see the lists and the figures and nothing that changes them. The service decides again on every write.
 *
 * Zone-scoped (Amendment 2 §1 "Zones"): the lists fan out across the configured zones, each row tagged with its own;
 * the pool composition, the rules and the figures are read from the console's working zone.
 */
type TabKey = 'grading' | 'workflow' | 'testimonials' | 'rubric' | 'refset';
type SubKey = 'graders' | 'batches' | 'quality' | 'rules';
const NONE: GradingCan = { configure: false, moderate: false, queue: false };

export function GradingPage() {
  const toast = useToast();
  const zone = useWorkingZone();
  const [tab, setTab] = useState<TabKey>('grading');
  const [sub, setSub] = useState<SubKey>('graders');
  const [can, setCan] = useState<GradingCan | null>(null);
  useEffect(() => {
    let live = true;
    setCan(null);
    getGradingMe(zone).then((m) => { if (live) setCan(m.can); }).catch((err) => {
      if (!live) return;
      // Unknown permissions fail CLOSED: the page still reads, and shows no mutator.
      setCan(NONE);
      const { title, detail } = toastFromError(err, 'Could not load your grading permissions');
      toast.push({ variant: 'error', title, detail });
    });
    return () => { live = false; };
  }, [zone, toast]);
  useZoneReadReport({ rows: null, failedZones: [], counted: false });
  const ctx = { can: can ?? NONE };

  const TABS: { key: TabKey; label: string; soon?: boolean }[] = [
    { key: 'grading', label: 'Grading' }, { key: 'workflow', label: 'Workflow' }, { key: 'testimonials', label: 'Testimonials' },
    { key: 'rubric', label: 'Rubric', soon: true }, { key: 'refset', label: 'Reference set', soon: true },
  ];
  const SUBS: { key: SubKey; label: string }[] = [
    { key: 'graders', label: 'Graders' }, { key: 'batches', label: 'Batches' }, { key: 'quality', label: 'Quality' }, { key: 'rules', label: 'Rules' },
  ];

  return (
    <div className="portal-page nx-grading" id="page-grading" data-testid="grading-page">
      <header className="page-header">
        <div><h2>Grading</h2><p className="page-intro">The people and the process behind the Engagement grade — the first impression real people give a customer's site. <b>Grading</b> runs community grading — graders, the two pools, the month's batches, quality and the queue rules; <b>Workflow</b> is the month's process end to end; <b>Testimonials</b> is every outcome story, its state and its credit; <b>Rubric</b> and <b>Reference set</b> are not yet designed.</p></div>
      </header>

      <nav className="portal-partners-tabs" aria-label="Grading sections">
        {TABS.map((t) => (
          <button key={t.key} type="button" className={`portal-partners-tab${tab === t.key ? ' active' : ''}${t.soon ? ' disabled' : ''}`}
            aria-current={tab === t.key ? 'page' : undefined} aria-disabled={t.soon || undefined} title={t.soon ? 'Planned — not yet designed' : undefined}
            onClick={() => { if (!t.soon) setTab(t.key); }}>
            {t.label}{t.soon && <> <span className="soon">not yet designed</span></>}
          </button>
        ))}
      </nav>

      {can === null ? <Loading text="Loading the Grading page…" /> : (
        <>
          {tab === 'grading' && (
            <section id="tab-grading">
              <div className="portal-inpage-tabs" role="tablist" aria-label="Grading tab sections">
                {SUBS.map((s) => (
                  <button key={s.key} type="button" role="tab" className={`portal-inpage-tab${sub === s.key ? ' portal-inpage-tab-active' : ''}`}
                    aria-selected={sub === s.key} onClick={() => setSub(s.key)}>{s.label}</button>
                ))}
              </div>
              {sub === 'graders' && <GradersTab zone={zone} />}
              {sub === 'batches' && <BatchesTab zone={zone} ctx={ctx} />}
              {sub === 'quality' && <QualityTab ctx={ctx} />}
              {sub === 'rules' && <RulesTab zone={zone} ctx={ctx} />}
            </section>
          )}
          {tab === 'workflow' && <WorkflowTab zone={zone} ctx={ctx} />}
          {tab === 'testimonials' && <TestimonialsTab ctx={ctx} />}
        </>
      )}
    </div>
  );
}
